// Human-friendly labels for enum values. Kept next to types so adding an
// enum member has an obvious single place to update.

import type {
  ActionItemStatus,
  ContactRole,
  DataAccessModel,
  DebarmentStatus,
  DocumentEntityType,
  DocumentType,
  InvestigatorRole,
  MilestoneSlug,
  OrganizationContactRole,
  PersonnelRole,
  PipelineStage,
  RiskSeverity,
  RiskStatus,
  SiteStatus,
  TargetTier,
  TranscriptStatus,
} from "./types";

export const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  qualified: "Qualified",
  contracting: "Contracting",
  activated: "Activated",
  declined: "Declined",
};

export const TARGET_TIER_LABELS: Record<TargetTier, string> = {
  not_targeted: "Not targeted",
  tier_1: "Tier 1",
  tier_2: "Tier 2",
  tier_3: "Tier 3",
};

export const DATA_ACCESS_MODEL_LABELS: Record<DataAccessModel, string> = {
  pull_integration: "Pull integration",
  abstraction: "Abstraction",
  hybrid: "Hybrid",
};

export const CONTACT_ROLE_LABELS: Record<ContactRole, string> = {
  it: "IT",
  therapeutic_area: "Therapeutic area",
  research: "Research",
  grants_contracts: "Grants & contracts",
  budget: "Budget",
};

export const TRANSCRIPT_STATUS_LABELS: Record<TranscriptStatus, string> = {
  pending: "Pending",
  processing: "Processing",
  complete: "Complete",
  failed: "Failed",
};

export const ACTION_ITEM_STATUS_LABELS: Record<ActionItemStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  done: "Done",
  cancelled: "Cancelled",
};

export const RISK_SEVERITY_LABELS: Record<RiskSeverity, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export const RISK_STATUS_LABELS: Record<RiskStatus, string> = {
  open: "Open",
  mitigated: "Mitigated",
  accepted: "Accepted",
  closed: "Closed",
};

export const PIPELINE_STAGES: PipelineStage[] = [
  "qualified",
  "contracting",
  "activated",
  "declined",
];

export const TARGET_TIERS: TargetTier[] = ["not_targeted", "tier_1", "tier_2", "tier_3"];

export const DATA_ACCESS_MODELS: DataAccessModel[] = [
  "pull_integration",
  "abstraction",
  "hybrid",
];

export const CONTACT_ROLES: ContactRole[] = [
  "it",
  "therapeutic_area",
  "research",
  "grants_contracts",
  "budget",
];

export const ACTION_ITEM_STATUSES: ActionItemStatus[] = [
  "open",
  "in_progress",
  "done",
  "cancelled",
];

export const RISK_SEVERITIES: RiskSeverity[] = ["low", "medium", "high", "critical"];

export const RISK_STATUSES: RiskStatus[] = ["open", "mitigated", "accepted", "closed"];

import type {
  LocationArtifactStatus,
  LocationArtifactType,
  ParticipationStatus,
  ParticipationTaskRole,
  ParticipationTaskStatus,
  StudyKind,
  StudyPhase,
  StudyStatus,
  StudyType,
} from "./types";

export const STUDY_KIND_LABELS: Record<StudyKind, string> = {
  master: "Master",
  pharma_child: "Pharma child",
};

export const STUDY_TYPE_LABELS: Record<StudyType, string> = {
  interventional: "Interventional",
  observational: "Observational",
};

export const STUDY_PHASE_LABELS: Record<StudyPhase, string> = {
  phase_i: "Phase I",
  phase_ii: "Phase II",
  phase_iii: "Phase III",
  phase_iv: "Phase IV",
};

export const STUDY_STATUS_LABELS: Record<StudyStatus, string> = {
  planning: "Planning",
  open_for_activation: "Open for activation",
  active: "Active",
  closed: "Closed",
};

export const PARTICIPATION_STATUS_LABELS: Record<ParticipationStatus, string> = {
  pending_kickoff: "Pending kickoff",
  active: "Active",
  on_hold: "On hold",
  activated: "Activated",
  cancelled: "Cancelled",
};

export const LOCATION_ARTIFACT_TYPE_LABELS: Record<LocationArtifactType, string> = {
  doa: "DOA (Delegation of Authority)",
  gcp_training: "GCP training",
  hipaa_training: "HIPAA training",
  investigator_cv: "Investigator CV",
  debarment_check: "Debarment check",
  site_capability_questionnaire: "Site capability questionnaire",
  site_training_log: "Site Training Log",
};

export const LOCATION_ARTIFACT_STATUS_LABELS: Record<LocationArtifactStatus, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  current: "Current",
  expiring: "Expiring",
  expired: "Expired",
};

export const PARTICIPATION_TASK_ROLE_LABELS: Record<ParticipationTaskRole, string> = {
  regulatory: "Regulatory",
  budget_contracts: "Budget & contracts",
  pi_liaison: "PI liaison",
  vendor_coordinator: "Vendor coordinator",
  project_manager: "Project manager",
};

export const PARTICIPATION_TASK_STATUS_LABELS: Record<ParticipationTaskStatus, string> = {
  todo: "To do",
  in_progress: "In progress",
  awaiting_external: "Awaiting external",
  done: "Done",
};

export const STUDY_KINDS: StudyKind[] = ["master", "pharma_child"];
export const STUDY_TYPES: StudyType[] = ["interventional", "observational"];
export const STUDY_PHASES: StudyPhase[] = ["phase_i", "phase_ii", "phase_iii", "phase_iv"];
export const STUDY_STATUSES: StudyStatus[] = [
  "planning",
  "open_for_activation",
  "active",
  "closed",
];

export const ORGANIZATION_CONTACT_ROLE_LABELS: Record<
  OrganizationContactRole,
  string
> = {
  executive_sponsor: "Executive sponsor",
  legal_counsel: "Legal counsel",
  central_it: "Central IT",
  research_liaison: "Research liaison",
  grants_office: "Grants office",
};

export const ORGANIZATION_CONTACT_ROLES: OrganizationContactRole[] = [
  "executive_sponsor",
  "legal_counsel",
  "central_it",
  "research_liaison",
  "grants_office",
];

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  irb_approval_letter: "IRB approval letter",
  signed_budget: "Signed budget",
  dua: "DUA",
  gcp_training_cert: "GCP training cert",
  hipaa_training_cert: "HIPAA training cert",
  investigator_cv: "Investigator CV",
  debarment_check: "Debarment check",
  form_1572: "1572 form",
  site_capability_questionnaire: "Site capability questionnaire",
  cta_agreement: "CTA agreement",
  master_msa: "Master MSA",
  protocol_amendment: "Protocol amendment",
  activation_letter: "Activation letter",
  confidentiality_agreement: "Confidentiality agreement",
  sponsor_sow: "Sponsor SOW",
  medical_license: "Medical License",
  site_training_log: "Site Training Log",
  delegation_log: "Delegation Log",
  conflict_of_interest: "Conflict of Interest",
  iata: "IATA dangerous goods cert",
  other: "Other",
};

export const DOCUMENT_TYPES: DocumentType[] = [
  "irb_approval_letter",
  "signed_budget",
  "dua",
  "gcp_training_cert",
  "hipaa_training_cert",
  "investigator_cv",
  "debarment_check",
  "form_1572",
  "site_capability_questionnaire",
  "cta_agreement",
  "master_msa",
  "protocol_amendment",
  "activation_letter",
  "confidentiality_agreement",
  "sponsor_sow",
  "medical_license",
  "site_training_log",
  "delegation_log",
  "conflict_of_interest",
  "iata",
  "other",
];

export const PERSONNEL_ROLE_LABELS: Record<PersonnelRole, string> = {
  pi: "PI",
  sub_i: "Sub-Investigator",
  crc: "Clinical Research Coordinator",
  rn: "Research Nurse",
  pm: "Project Manager",
  ra: "Research Assistant",
  other: "Other",
};

export const PERSONNEL_ROLES: PersonnelRole[] = [
  "pi", "sub_i", "crc", "rn", "pm", "ra", "other",
];

export const DOCUMENT_ENTITY_TYPE_LABELS: Record<DocumentEntityType, string> = {
  organization: "Organization",
  site: "Site",
  location: "Location",
  location_artifact: "Location artifact",
  study: "Study",
  disease_area: "Disease area",
  participation: "Participation",
  participation_task: "Participation task",
  investigator: "Investigator",
  location_personnel: "Location personnel",
};

export const DOCUMENT_AUDIT_EVENT_LABELS: Record<string, string> = {
  document_created: "Created",
  document_version_added: "New version uploaded",
  document_metadata_changed: "Metadata changed",
  document_soft_deleted: "Soft-deleted",
  document_restored: "Restored",
  document_viewed: "Viewed",
  document_downloaded: "Downloaded",
  document_version_viewed: "Opened in viewer",
};

export const ALLOWED_DOCUMENT_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
] as const;

export const MAX_DOCUMENT_FILE_SIZE_BYTES = 50 * 1024 * 1024;

export const DEBARMENT_STATUS_LABELS: Record<DebarmentStatus, string> = {
  not_checked: "Not checked",
  clear: "Clear",
  debarred: "Debarred",
};

export const DEBARMENT_STATUSES: DebarmentStatus[] = [
  "not_checked",
  "clear",
  "debarred",
];

export const INVESTIGATOR_ROLE_LABELS: Record<InvestigatorRole, string> = {
  pi: "Principal Investigator",
  sub_investigator: "Sub-Investigator",
};

export const INVESTIGATOR_ROLES: InvestigatorRole[] = ["pi", "sub_investigator"];

export const MILESTONE_LABELS: Record<MilestoneSlug, string> = {
  feasibility: "Feasibility",
  irb_approval: "IRB Approval",
  budget: "Budget",
  contract: "Contract",
  startup_packet: "Start-up Packet",
  site_activation: "Site Activation",
};

export const SITE_STATUS_LABELS: Record<SiteStatus, string> = {
  start_up: "Start-up",
  active: "Active",
  closed: "Closed",
};

/** Zero-pad a site ``display_id`` to three digits for rendering.
 * Returns an em-dash when the id is null/undefined so callers can
 * concatenate without a guard. The DB stores ``display_id`` as int;
 * this is the single place we decide how to render it. */
export function formatSiteDisplayId(displayId: number | null | undefined): string {
  if (displayId === null || displayId === undefined) return "—";
  return String(displayId).padStart(3, "0");
}
